/* ══════════════════════════════════════════════════
 *  Admin React Query hooks
 *  Groups: Users, Pipelines, Fields, Defendants,
 *  Tickets, DataImport, DocUpload, Ledger,
 *  Reports, RecycleBin, Templates, Email, BLP
 * ══════════════════════════════════════════════════ */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type {
  AdminUser,
  OffboardResult,
  AdminFeature,
  AdminCaseType,
  UserGroup,
  UserGroupDetail,
  CustomRole,
  CustomRoleDetail,
  Pipeline,
  PipelineStage,
  FieldDefinition,
  FieldCategory,
  ValidationRule,
  Defendant,
  DefendantAddress,
  Ticket,
  TicketStats,
  ImportHistoryEntry,
  LedgerDocument,
  LedgerFilters,
  PresetReport,
  RecycleBinItem,
  RecycleBinStats,
  DraftTemplate,
  EmailTemplate,
  BLPTemplate,
  BLPLetterhead,
  BLPEmailConfig,
  BulkJob,
  BulkJobHistoryResponse,
  ConnectorTemplate,
  ConnectorInstance,
  WebhookEndpoint,
  WebhookSubscription,
  WebhookDelivery,
  IntegrationAuditEntry,
  TestConnectionResult,
  PortalDashboardStats,
  PortalUser,
  PortalUserSession,
  PortalEmailTemplate,
  PortalBranding,
  PortalAuditEntry,
  GovernanceCapacity,
  SystemConfigEntry,
  FirmBrandingMap,
  FirmBrandingUpdate,
  FirmLogo,
  FirmLetterhead,
  StageGuard,
} from '../types/admin';

/* ═══════════════════════════════════════════
 *  1. USER MANAGEMENT
 * ═══════════════════════════════════════════ */

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/users');
      if (data.success && data.users) {
        // Map BE fields to AdminUser shape
        return data.users.map((u: Record<string, unknown>) => ({
          username: u.username,
          email: (u as Record<string, unknown>).email || '',
          full_name: u.full_name || '',
          role: u.role || 'user',
          status: u.is_active === false ? 'deactivated' : 'active',
          offboarded_at: (u.offboarded_at as string | null) ?? null,
          position_in_company: u.position_in_company || '',
          features: Array.isArray(u.permissions)
            ? (u.permissions as { feature_name: string }[]).map((p) => p.feature_name)
            : [],
          case_type_restrictions: Array.isArray(u.allowed_case_types)
            ? (u.allowed_case_types as number[])
            : [],
          permission_count: u.permission_count ?? 0,
          // May the viewer change this user? Set by the server from the role ladder.
          can_manage: u.can_manage !== false,
        })) as AdminUser[];
      }
      if (Array.isArray(data)) return data as AdminUser[];
      return [];
    },
    staleTime: 60_000,
  });
}

export function useAdminFeatures() {
  return useQuery<AdminFeature[]>({
    queryKey: ['admin', 'features'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/features');
      // BE may return { features: { "CategoryName": [{...},...], ... } } (grouped)
      // or { features: [...] } (flat array)
      const raw = data.features ?? data.data ?? [];
      if (Array.isArray(raw)) return raw as AdminFeature[];
      // Grouped object → flatten with category attached
      const flat: AdminFeature[] = [];
      for (const [cat, items] of Object.entries(raw)) {
        for (const f of items as AdminFeature[]) {
          flat.push({ ...f, category: f.category || cat });
        }
      }
      return flat;
    },
    staleTime: 5 * 60_000,
  });
}

export function useAdminCaseTypes() {
  return useQuery<AdminCaseType[]>({
    queryKey: ['admin', 'caseTypes'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/case-types');
      if (data.success && data.data) return data.data as AdminCaseType[];
      return [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      username: string;
      role?: string;
      features?: string[];
      case_type_restrictions?: number[];
    }) => {
      const { data } = await api.put(
        `/api/admin/users/${encodeURIComponent(payload.username)}`,
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/** Deactivate (Cognito disable + end all sessions) or reactivate a staff user. History is kept. */
export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ username, active }: { username: string; active: boolean }) => {
      const path = `/api/admin/users/${encodeURIComponent(username)}`;
      const { data } = active
        ? await api.post(`${path}/activate`)
        : await api.delete(`${path}/deactivate`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/** End every open session for a staff user on all devices; the account stays active. */
export function useRevokeUserSessions() {
  return useMutation({
    mutationFn: async (username: string) => {
      const { data } = await api.post(
        `/api/admin/users/${encodeURIComponent(username)}/revoke-sessions`,
      );
      return data;
    },
  });
}

/**
 * Offboard a leaver: lock out, remove access, hand their open tasks and cases to a
 * colleague, and write one audit record. Running it again retries any failed step.
 */
export function useOffboardUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      username,
      reason,
      handoverTo,
    }: {
      username: string;
      reason: string;
      handoverTo: string;
    }) => {
      const { data } = await api.post<OffboardResult>(
        `/api/admin/users/${encodeURIComponent(username)}/offboard`,
        { reason, handover_to: handoverTo },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/** Fetch single user detail with all_features + has_permission flags */
export function useAdminUserDetail(username: string | null) {
  return useQuery<{
    username: string;
    role: string;
    full_name?: string;
    position_in_company?: string;
    own_cases_only: boolean;
    all_features: AdminFeature[];
    allowed_case_types: number[];
    groups: { id: number; name: string; description?: string; assigned_at: string }[];
  }>({
    queryKey: ['admin', 'userDetail', username],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/users/${encodeURIComponent(username!)}`);
      const u = data.user ?? data;
      return {
        username: u.username,
        role: u.role ?? 'user',
        full_name: u.full_name,
        position_in_company: u.position_in_company,
        all_features: Array.isArray(u.all_features) ? u.all_features : [],
        allowed_case_types: Array.isArray(u.allowed_case_types)
          ? (u.allowed_case_types as unknown[]).map((v) => Number(v)).filter((n) => !isNaN(n))
          : [],
        own_cases_only: u.own_cases_only === true,
        groups: Array.isArray(u.groups) ? u.groups : [],
      };
    },
    enabled: !!username,
    staleTime: 30_000,
  });
}

/** Grant or revoke a single permission */
export function useTogglePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      username,
      feature_name,
      grant,
    }: {
      username: string;
      feature_name: string;
      grant: boolean;
    }) => {
      const { data } = grant
        ? await api.post(`/api/admin/users/${encodeURIComponent(username)}/permissions`, {
            feature_name,
          })
        : await api.delete(
            `/api/admin/users/${encodeURIComponent(username)}/permissions/${encodeURIComponent(feature_name)}`,
          );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'userDetail', vars.username] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Update user role */
export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ username, role }: { username: string; role: string }) => {
      const { data } = await api.put(`/api/admin/users/${encodeURIComponent(username)}/role`, {
        role,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'userDetail', vars.username] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Update user case type restrictions */
export function useUpdateUserCaseTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      username,
      case_type_ids,
    }: {
      username: string;
      case_type_ids: number[];
    }) => {
      const { data } = await api.put(
        `/api/admin/users/${encodeURIComponent(username)}/case-types`,
        { case_types: case_type_ids },
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'userDetail', vars.username] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Toggle own_cases_only data-scoping flag */
export function useUpdateOwnCasesOnly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      username,
      own_cases_only,
    }: {
      username: string;
      own_cases_only: boolean;
    }) => {
      const { data } = await api.put(
        `/api/admin/users/${encodeURIComponent(username)}/own-cases-only`,
        { own_cases_only },
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'userDetail', vars.username] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/** Create new admin user */
export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      username: string;
      full_name: string;
      position_in_company?: string;
      role: string;
    }) => {
      const { data } = await api.post('/api/admin/users', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/* ═══════════════════════════════════════════
 *  1b. USER GROUPS
 * ═══════════════════════════════════════════ */

export function useAdminGroups() {
  return useQuery<UserGroup[]>({
    queryKey: ['admin', 'groups'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/groups');
      return (data.groups ?? []) as UserGroup[];
    },
    staleTime: 60_000,
  });
}

export function useAdminGroupDetail(groupId: number | null) {
  return useQuery<UserGroupDetail>({
    queryKey: ['admin', 'groupDetail', groupId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/groups/${groupId}`);
      return data.group as UserGroupDetail;
    },
    enabled: groupId !== null,
    staleTime: 30_000,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const { data } = await api.post('/api/admin/groups', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'groups'] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: number; name?: string; description?: string }) => {
      const { data } = await api.put(`/api/admin/groups/${id}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groupDetail', vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/groups/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'groups'] }),
  });
}

export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, username }: { groupId: number; username: string }) => {
      const { data } = await api.post(`/api/admin/groups/${groupId}/members`, { username });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groupDetail', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, username }: { groupId: number; username: string }) => {
      const { data } = await api.delete(
        `/api/admin/groups/${groupId}/members/${encodeURIComponent(username)}`,
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groupDetail', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useToggleGroupPermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      feature_name,
      grant,
    }: {
      groupId: number;
      feature_name: string;
      grant: boolean;
    }) => {
      const { data } = grant
        ? await api.post(`/api/admin/groups/${groupId}/permissions`, { feature_name })
        : await api.delete(
            `/api/admin/groups/${groupId}/permissions/${encodeURIComponent(feature_name)}`,
          );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groupDetail', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  1c. CUSTOM ROLES (Phase 4)
 * ═══════════════════════════════════════════ */

export function useAdminRoles() {
  return useQuery<CustomRole[]>({
    queryKey: ['admin', 'roles'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/roles');
      return (data.roles ?? []) as CustomRole[];
    },
    staleTime: 60_000,
  });
}

export function useAdminRoleDetail(roleId: number | null) {
  return useQuery<CustomRoleDetail>({
    queryKey: ['admin', 'roleDetail', roleId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/roles/${roleId}`);
      return data.role as CustomRoleDetail;
    },
    enabled: roleId !== null,
    staleTime: 30_000,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const { data } = await api.post('/api/admin/roles', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: number; name?: string; description?: string }) => {
      const { data } = await api.put(`/api/admin/roles/${id}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roleDetail', vars.id] });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/roles/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

export function useAddRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, username }: { roleId: number; username: string }) => {
      const { data } = await api.post(`/api/admin/roles/${roleId}/members`, { username });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'roleDetail', vars.roleId] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
}

export function useRemoveRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, username }: { roleId: number; username: string }) => {
      const { data } = await api.delete(
        `/api/admin/roles/${roleId}/members/${encodeURIComponent(username)}`,
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'roleDetail', vars.roleId] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
}

export function useToggleRolePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roleId,
      feature_name,
      grant,
    }: {
      roleId: number;
      feature_name: string;
      grant: boolean;
    }) => {
      const { data } = grant
        ? await api.post(`/api/admin/roles/${roleId}/permissions`, { feature_name })
        : await api.delete(
            `/api/admin/roles/${roleId}/permissions/${encodeURIComponent(feature_name)}`,
          );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'roleDetail', vars.roleId] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  2. PIPELINE MANAGEMENT
 * ═══════════════════════════════════════════ */

export function usePipelines() {
  return useQuery<Pipeline[]>({
    queryKey: ['admin', 'pipelines'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/pipelines');
      return (data.pipelines ?? data.data ?? []) as Pipeline[];
    },
    staleTime: 2 * 60_000,
  });
}

export function usePipelineStages(pipelineId: number | null) {
  return useQuery<PipelineStage[]>({
    queryKey: ['admin', 'pipelineStages', pipelineId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/pipelines/${pipelineId}`);
      const pipeline = data.data ?? data.pipeline ?? data;
      const rawStages = pipeline.stages ?? [];
      // Map stage_order → sort_order for consistency with PipelineStage type
      return rawStages.map((s: Record<string, unknown>) => ({
        ...s,
        sort_order: s.sort_order ?? s.stage_order ?? 0,
      })) as PipelineStage[];
    },
    enabled: !!pipelineId,
    staleTime: 60_000,
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Pipeline>) => {
      const { data } = await api.post('/api/admin/pipelines', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'pipelines'] }),
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: Partial<Pipeline> & { id: number }) => {
      const { data } = await api.put(`/api/admin/pipelines/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'pipelines'] }),
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/pipelines/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'pipelines'] }),
  });
}

export function useCreateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<PipelineStage> & { pipeline_id: number }) => {
      const { data } = await api.post(
        `/api/admin/pipelines/${payload.pipeline_id}/stages`,
        payload,
      );
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['admin', 'pipelineStages', vars.pipeline_id] }),
  });
}

export function useUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pipeline_id,
      stage_id,
      ...rest
    }: Partial<PipelineStage> & { pipeline_id: number; stage_id: number }) => {
      const { data } = await api.put(
        `/api/admin/pipelines/${pipeline_id}/stages/${stage_id}`,
        rest,
      );
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['admin', 'pipelineStages', vars.pipeline_id] }),
  });
}

export function useDeleteStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipeline_id, stage_id }: { pipeline_id: number; stage_id: number }) => {
      const { data } = await api.delete(`/api/admin/pipelines/${pipeline_id}/stages/${stage_id}`);
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['admin', 'pipelineStages', vars.pipeline_id] }),
  });
}

export function useReorderStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pipeline_id,
      stages,
    }: {
      pipeline_id: number;
      stages: { stage_id: number; stage_order: number }[];
    }) => {
      const { data } = await api.put(`/api/admin/pipelines/${pipeline_id}/stages/reorder`, {
        stages,
      });
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['admin', 'pipelineStages', vars.pipeline_id] }),
  });
}

/* ═══════════════════════════════════════════
 *  3. CONFIGURE DATA FIELDS
 * ═══════════════════════════════════════════ */

export function useFieldDefinitions() {
  return useQuery<FieldDefinition[]>({
    queryKey: ['admin', 'fields'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/fields');
      const raw = data.data ?? [];
      return (raw as Record<string, unknown>[]).map((f) => ({
        ...f,
        key: f.key ?? f.field_key ?? '',
        label: f.name ?? f.field_name ?? f.label ?? '',
        name: f.name ?? f.field_name ?? '',
        type: f.field_type ?? f.type ?? 'text',
        field_type: f.field_type ?? f.type ?? 'text',
        category: f.category_name ?? f.category ?? '',
        category_name: f.category_name ?? f.category ?? '',
        source_table: f.source_table ?? 'cases',
        storage_mode: (f.storage_mode as 'eav' | 'column' | undefined) ?? 'column',
        is_unique: Boolean(f.is_unique),
        placeholder_text:
          (f.placeholder_text as string | undefined) ?? (f.placeholder as string | undefined) ?? '',
        help_text: (f.help_text as string | undefined) ?? '',
        default_value: (f.default_value as string | undefined) ?? '',
        case_type_names: Array.isArray(f.case_type_names) ? f.case_type_names : [],
        case_types: Array.isArray(f.case_types)
          ? (f.case_types as unknown[]).map((v) => Number(v)).filter((n: number) => !isNaN(n))
          : [],
      })) as FieldDefinition[];
    },
    staleTime: 2 * 60_000,
  });
}

export function useFieldCategories() {
  return useQuery<FieldCategory[]>({
    queryKey: ['admin', 'fieldCategories'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/categories');
      return (data.data ?? []) as FieldCategory[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useValidationRules() {
  return useQuery<ValidationRule[]>({
    queryKey: ['admin', 'validationRules'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/validation-rules');
      return (data.data ?? []) as ValidationRule[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<FieldDefinition>) => {
      const { data } = await api.post('/api/fieldconfig/fields', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fields'] }),
  });
}

export function useUpdateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: Partial<FieldDefinition> & { id: number }) => {
      const { data } = await api.put(`/api/fieldconfig/fields/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fields'] }),
  });
}

export function useDeleteField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/fieldconfig/fields/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fields'] }),
  });
}

/* ── Field Options CRUD ── */
export interface FieldOption {
  id?: number;
  value: string;
  label: string;
  display_order?: number;
}

export function useFieldOptions(fieldId: number | null) {
  return useQuery<FieldOption[]>({
    queryKey: ['admin', 'fieldOptions', fieldId],
    queryFn: async () => {
      if (!fieldId) return [];
      const { data } = await api.get(`/api/fieldconfig/fields/${fieldId}/options`);
      return (data.data ?? []) as FieldOption[];
    },
    enabled: fieldId != null,
    staleTime: 30_000,
  });
}

export function useCreateFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fieldId, ...payload }: FieldOption & { fieldId: number }) => {
      const { data } = await api.post(`/api/fieldconfig/fields/${fieldId}/options`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'fieldOptions', variables.fieldId] });
      qc.invalidateQueries({ queryKey: ['admin', 'fields'] });
    },
  });
}

export function useUpdateFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      fieldId,
      id,
      ...payload
    }: FieldOption & { fieldId: number; id: number }) => {
      const { data } = await api.put(`/api/fieldconfig/fields/${fieldId}/options/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'fieldOptions', variables.fieldId] });
      qc.invalidateQueries({ queryKey: ['admin', 'fields'] });
    },
  });
}

export function useDeleteFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fieldId, id }: { fieldId: number; id: number }) => {
      const { data } = await api.delete(`/api/fieldconfig/fields/${fieldId}/options/${id}`);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'fieldOptions', variables.fieldId] });
      qc.invalidateQueries({ queryKey: ['admin', 'fields'] });
    },
  });
}

/** Update case_types array on a field definition (separate endpoint) */
export function useUpdateFieldCaseTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, caseTypeIds }: { id: number; caseTypeIds: number[] }) => {
      const { data } = await api.put(`/api/fieldconfig/fields/${id}/case-types`, { caseTypeIds });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fields'] }),
  });
}

/** Phase 4: governance capacity snapshot (fields/cases/contacts). */
export function useGovernanceCapacity() {
  return useQuery<GovernanceCapacity>({
    queryKey: ['admin', 'governance', 'capacity'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/governance/capacity');
      return data.data as GovernanceCapacity;
    },
    staleTime: 30_000,
  });
}

/** Phase 4: list system_config rows (governance limits + thresholds). */
export function useSystemConfig() {
  return useQuery<SystemConfigEntry[]>({
    queryKey: ['admin', 'systemConfig'],
    queryFn: async () => {
      const { data } = await api.get('/api/fieldconfig/system-config');
      return (data.data ?? []) as SystemConfigEntry[];
    },
    staleTime: 60_000,
  });
}

/** Phase 4: update one system_config row (numeric governance values). */
export function useUpdateSystemConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string | number }) => {
      const { data } = await api.put(`/api/fieldconfig/system-config/${encodeURIComponent(key)}`, {
        value,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'systemConfig'] });
      qc.invalidateQueries({ queryKey: ['admin', 'governance', 'capacity'] });
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<FieldCategory>) => {
      const { data } = await api.post('/api/fieldconfig/categories', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fieldCategories'] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: Partial<FieldCategory> & { id: number }) => {
      const { data } = await api.put(`/api/fieldconfig/categories/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fieldCategories'] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/fieldconfig/categories/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fieldCategories'] }),
  });
}

export function useCreateValidationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ValidationRule>) => {
      const { data } = await api.post('/api/fieldconfig/validation-rules', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'validationRules'] }),
  });
}

export function useUpdateValidationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: Partial<ValidationRule> & { id: number }) => {
      const { data } = await api.put(`/api/fieldconfig/validation-rules/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'validationRules'] }),
  });
}

export function useDeleteValidationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/fieldconfig/validation-rules/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'validationRules'] }),
  });
}

/* ═══════════════════════════════════════════
 *  4. DEFENDANT DATA
 * ═══════════════════════════════════════════ */

export function useDefendants(page = 1, search = '', limit = 50) {
  return useQuery<{
    defendants: Defendant[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>({
    queryKey: ['admin', 'defendants', page, search, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/defendants?${params.toString()}`);
      // Map address.id → address_id, inject defendant_id for consistency
      const defendants = (data.defendants ?? data.data ?? []).map((d: Record<string, unknown>) => ({
        ...d,
        addresses: Array.isArray(d.addresses)
          ? (d.addresses as Record<string, unknown>[]).map((a) => ({
              ...a,
              address_id: a.address_id ?? a.id,
              defendant_id: d.defendant_id,
            }))
          : [],
      })) as Defendant[];
      return {
        defendants,
        pagination: data.pagination ?? { page, limit: 50, total: defendants.length, totalPages: 1 },
      };
    },
    staleTime: 2 * 60_000,
  });
}

export function useCreateDefendant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Defendant>) => {
      const { data } = await api.post('/api/defendants', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'defendants'] }),
  });
}

export function useUpdateDefendant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      defendant_id,
      ...rest
    }: Partial<Defendant> & { defendant_id: number }) => {
      const { data } = await api.put(`/api/defendants/${defendant_id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'defendants'] }),
  });
}

export function useDeleteDefendant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/defendants/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'defendants'] }),
  });
}

export function useSaveDefendantAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DefendantAddress) => {
      const url = payload.address_id
        ? `/api/defendants/${payload.defendant_id}/addresses/${payload.address_id}`
        : `/api/defendants/${payload.defendant_id}/addresses`;
      const method = payload.address_id ? 'put' : 'post';
      const { data } = await api[method](url, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'defendants'] }),
  });
}

export function useDeleteDefendantAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      defendant_id,
      address_id,
    }: {
      defendant_id: number;
      address_id: number;
    }) => {
      const { data } = await api.delete(`/api/defendants/${defendant_id}/addresses/${address_id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'defendants'] }),
  });
}

/* ═══════════════════════════════════════════
 *  5. ISSUE TICKETS
 * ═══════════════════════════════════════════ */

export function useTickets() {
  return useQuery<Ticket[]>({
    queryKey: ['admin', 'tickets'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/tickets');
      const raw = data.tickets ?? data.data ?? [];
      // Map DynamoDB fields to clean Ticket interface
      return raw.map((t: Record<string, unknown>) => ({
        id: t.ticketId ?? t.id,
        title: t.location ?? t.title ?? '',
        description: t.issueDescription ?? t.description ?? '',
        status: t.status ?? 'New Ticket',
        submitter: t.username ?? t.submitter ?? '',
        created_at: t.createdAt ?? t.created_at ?? '',
        updated_at: t.updatedAt ?? t.updated_at,
        resolution_notes: t.resolutionNotes ?? t.resolution_notes,
        submissionDate: t.submissionDate,
        submissionTime: t.submissionTime,
      })) as Ticket[];
    },
    staleTime: 30_000,
  });
}

/** Compute ticket stats client-side from the tickets query (no separate BE endpoint) */
export function useTicketStats() {
  const { data: tickets = [] } = useTickets();
  const stats: TicketStats = {
    total: tickets.length,
    new: tickets.filter((t) => t.status === 'New Ticket' || t.status === ('New' as string)).length,
    inProgress: tickets.filter((t) => t.status === 'In Progress').length,
    resolved: tickets.filter((t) => t.status === 'Resolved').length,
  };
  return { data: stats, isLoading: false };
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { title: string; description: string }) => {
      // Map to backend expected fields
      const { data } = await api.post('/api/issue-tickets', {
        location: payload.title,
        issueDescription: payload.description,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tickets'] });
    },
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      resolution_notes,
    }: {
      id: string;
      status?: string;
      resolution_notes?: string;
      [key: string]: unknown;
    }) => {
      const { data } = await api.put(`/api/admin/tickets/${encodeURIComponent(id)}`, {
        status,
        resolutionNotes: resolution_notes,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tickets'] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  6. DATA IMPORT
 * ═══════════════════════════════════════════ */

export function useImportHistory() {
  return useQuery<ImportHistoryEntry[]>({
    queryKey: ['admin', 'importHistory'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/import/history');
      return (data.history ?? data.data ?? []) as ImportHistoryEntry[];
    },
    staleTime: 60_000,
  });
}

export function useImportUpload() {
  return useMutation({
    mutationFn: async (payload: FormData | Record<string, unknown>) => {
      const isForm = payload instanceof FormData;
      const { data } = await api.post('/api/admin/import/upload', payload, {
        headers: { 'Content-Type': isForm ? 'multipart/form-data' : 'application/json' },
      });
      return data;
    },
  });
}

// ─── Types for the column-mapping preview flow ───────────────────────────────

export interface ImportFieldOption {
  key: string;
  label: string;
  type: string;
}

export interface ImportSuggestedMapping {
  csvIndex: number;
  csvColumnName: string;
  field: string; // field_key or 'SKIP'
  confidence: 'exact' | 'pattern' | 'none';
}

export interface ImportPreviewResult {
  headers: string[];
  totalRows: number;
  sampleRows: string[][];
  suggestedMappings: ImportSuggestedMapping[];
  fieldOptionsGrouped: Record<string, ImportFieldOption[]>;
}

/** POST /api/admin/import/preview — parse CSV and get mapping suggestions without writing any data */
export function useImportPreview() {
  return useMutation<ImportPreviewResult, Error, FormData>({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/admin/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data.success) throw new Error(data.message ?? 'Preview failed');
      return data as ImportPreviewResult;
    },
  });
}

/** POST /api/admin/import/upload — upload file with user-confirmed mappings */
export function useImportWithMappings() {
  return useMutation<
    {
      successCount?: number;
      failedCount?: number;
      details?: unknown;
      jobId?: string;
      total?: number;
    },
    Error,
    { file: File; mappings: ImportSuggestedMapping[]; mode: 'upload' | 'update' }
  >({
    mutationFn: async ({ file, mappings, mode }) => {
      const fd = new FormData();
      fd.append('file', file);
      // Send only non-SKIP mappings as a JSON string so the backend can use them directly
      fd.append('confirmed_mappings', JSON.stringify(mappings.filter((m) => m.field !== 'SKIP')));
      const endpoint = mode === 'upload' ? '/api/admin/import/upload' : '/api/admin/bulk-update';
      const { data } = await api.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

export function useBulkUpdate() {
  return useMutation({
    mutationFn: async (payload: FormData | Record<string, unknown>) => {
      const isForm = payload instanceof FormData;
      const { data } = await api.post('/api/admin/bulk-update', payload, {
        headers: { 'Content-Type': isForm ? 'multipart/form-data' : 'application/json' },
      });
      return data;
    },
  });
}

export interface BulkUpdateJob {
  jobId: string;
  status: 'processing' | 'complete' | 'failed';
  total: number;
  completed: number;
  failed: number;
  result?: { successCount: number; failedCount: number; details: unknown };
  error?: string;
}

export function useBulkUpdateJobStatus(jobId: string | null) {
  return useQuery<BulkUpdateJob>({
    queryKey: ['admin', 'bulkUpdateJob', jobId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/bulk-update/status/${jobId}`);
      return data as BulkUpdateJob;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const d = query.state.data as BulkUpdateJob | undefined;
      if (d && (d.status === 'complete' || d.status === 'failed')) return false;
      return 2000;
    },
    staleTime: 0,
  });
}

/* ═══════════════════════════════════════════
 *  7. DOCUMENT UPLOAD (batch)
 * ═══════════════════════════════════════════ */

export function useChunkUpload() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/explorer/uploadChunk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

/* ── Presigned-URL Bulk Upload hooks ── */

export function useBulkUploadStart() {
  return useMutation({
    mutationFn: async (payload: {
      files: { name: string; size: number }[];
      defaultCategoryId?: number | null;
    }) => {
      const { data } = await api.post('/api/admin/upload-documents/start', payload);
      return data;
    },
  });
}

export function useBulkUploadValidate() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { data } = await api.post(`/api/admin/upload-documents/validate/${sessionId}`);
      return data;
    },
  });
}

export function useBulkUploadConfirm() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      results,
    }: {
      sessionId: string;
      results: {
        filename: string;
        success: boolean;
        error?: string;
        contentHash?: string;
        pageCount?: number | null;
      }[];
    }) => {
      const { data } = await api.post(`/api/admin/upload-documents/confirm/${sessionId}`, {
        results,
      });
      return data;
    },
  });
}

export function useBulkUploadComplete() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { data } = await api.post(`/api/admin/upload-documents/complete/${sessionId}`);
      return data;
    },
  });
}

export function useBulkUploadActive() {
  return useQuery<{ active: boolean; session?: Record<string, unknown> }>({
    queryKey: ['admin', 'bulk-upload', 'active'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/upload-documents/active');
      return data;
    },
    staleTime: 10_000,
  });
}

export function useBulkUploadProgress(sessionId: string | null, enabled: boolean) {
  return useQuery<{ progress: number; estimatedRemaining?: number }>({
    queryKey: ['admin', 'bulk-upload', 'progress', sessionId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/upload-documents/progress/${sessionId}`);
      return data;
    },
    enabled: !!sessionId && enabled,
    refetchInterval: enabled ? 3000 : false,
    staleTime: 0,
  });
}

export function useBulkUploadRefreshUrls() {
  return useMutation({
    mutationFn: async ({ sessionId, fileIds }: { sessionId: string; fileIds?: string[] }) => {
      const { data } = await api.post(`/api/admin/upload-documents/refresh/${sessionId}`, {
        fileIds,
      });
      return data as { refreshed: { id: string; presignedUrl: string }[]; urlsGeneratedAt: string };
    },
  });
}

/* ═══════════════════════════════════════════
 *  8. DOCUMENT PRINT LEDGER
 * ═══════════════════════════════════════════ */

export function useLedgerDocuments(filters: LedgerFilters) {
  return useQuery<{
    documents: LedgerDocument[];
    total: number;
    hasMore: boolean;
    nextPageToken?: string;
  }>({
    queryKey: ['admin', 'ledger', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '') params.append(k, String(v));
      });
      const { data } = await api.get(`/api/admin/documents?${params.toString()}`);
      /* Map DynamoDB PascalCase to our snake_case LedgerDocument type */
      const raw = (data.documents ?? data.data ?? data.Items ?? []) as Record<string, unknown>[];
      const documents: LedgerDocument[] = raw.map((d) => ({
        id: (d.id ?? d.DocumentID ?? d.documentId ?? 0) as number,
        case_ref: (d.case_ref ?? d.CaseRef ?? d.caseRef ?? '') as string,
        case_type: (d.case_type ?? d.CaseType ?? d.caseType ?? '') as string,
        document_type: (d.document_type ?? d.DocumentType ?? d.documentType ?? '') as string,
        document_name: (d.document_name ?? d.FileName ?? d.fileName ?? d.file_name ?? '') as string,
        status: (d.status ?? d.Status ?? 'pending') as LedgerDocument['status'],
        generated_at: (d.generated_at ??
          d.GeneratedAt ??
          d.generatedAt ??
          d.created_at ??
          '') as string,
        generated_by: (d.generated_by ?? d.GeneratedBy ?? d.generatedBy ?? '') as string,
        s3_key: (d.s3_key ?? d.S3Key ?? d.s3Key ?? '') as string,
      }));
      return {
        documents,
        total: (data.totalCount ?? data.total ?? data.count ?? documents.length) as number,
        hasMore: !!data.hasMore,
        nextPageToken: data.nextPageToken ?? data.lastEvaluatedKey ?? undefined,
      };
    },
    staleTime: 30_000,
  });
}

export function useUpdateLedgerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number | string; status: string }) => {
      const { data } = await api.put(`/api/admin/documents/${id}/status`, { status });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger'] }),
  });
}

export function useBulkUpdateLedgerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentIds,
      newStatus,
    }: {
      documentIds: (number | string)[];
      newStatus: string;
    }) => {
      const { data } = await api.post('/api/admin/documents/bulk-update-status', {
        documentIds,
        newStatus,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger'] }),
  });
}

export function useBulkDownloadDocuments() {
  return useMutation({
    mutationFn: async (documentIds: (number | string)[]) => {
      const res = await api.post(
        '/api/admin/documents/bulk-download',
        { documentIds },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useLedgerStats() {
  return useQuery({
    queryKey: ['admin', 'ledger', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/documents/stats');
      return data.stats;
    },
    staleTime: 60_000,
  });
}

export function useDocumentCategories() {
  return useQuery({
    queryKey: ['admin', 'documentCategories'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/documents/categories');
      return (data.categories ?? []) as import('../types/admin').DocumentCategory[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateDocumentCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      slug: string;
      description?: string;
      colour?: string;
      sort_order?: number;
    }) => {
      const { data } = await api.post('/api/admin/documents/categories', payload);
      return data.category as import('../types/admin').DocumentCategory;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'documentCategories'] }),
  });
}

export function useUpdateDocumentCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...rest
    }: {
      id: number;
      name?: string;
      description?: string;
      colour?: string;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data } = await api.put(`/api/admin/documents/categories/${id}`, rest);
      return data.category as import('../types/admin').DocumentCategory;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'documentCategories'] }),
  });
}

export function useDeleteDocumentCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/documents/categories/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'documentCategories'] }),
  });
}

export function useSoftDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string | number; reason?: string }) => {
      const { data } = await api.patch(`/api/admin/documents/${id}/soft-delete`, { reason });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger'] }),
  });
}

export function useMarkForPrinting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mark }: { id: string | number; mark: boolean }) => {
      const { data } = await api.patch(`/api/admin/documents/${id}/mark-for-printing`, { mark });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger'] }),
  });
}

export function useBulkAssignCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentIds,
      categoryId,
    }: {
      documentIds: (number | string)[];
      categoryId: number;
    }) => {
      const { data } = await api.post('/api/admin/documents/bulk-assign-category', {
        documentIds,
        categoryId,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger'] }),
  });
}

export function useTriggerReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/admin/ledger/reconcile');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ledger', 'reconciliation'] }),
  });
}

export function useReconciliationStatus() {
  return useQuery({
    queryKey: ['admin', 'ledger', 'reconciliation'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/ledger/reconciliation-status');
      return (data.runs ?? []) as import('../types/admin').ReconciliationRun[];
    },
    staleTime: 30_000,
  });
}

/* ═══════════════════════════════════════════
 *  9. PRESET REPORTS
 * ═══════════════════════════════════════════ */

export function usePresetReports() {
  return useQuery<PresetReport[]>({
    queryKey: ['admin', 'presetReports'],
    queryFn: async () => {
      const { data } = await api.get('/api/preset-reports');
      return (data.reports ?? data.data ?? []) as PresetReport[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useRunReport() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/api/preset-reports/${id}/select`);
      return data;
    },
  });
}

export function useReportAggregate(id: number | null) {
  return useQuery<{ data: Record<string, unknown>[]; rowCount: number }>({
    queryKey: ['admin', 'reportAggregate', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/preset-reports/${id}/aggregate`);
      return { data: data.data ?? [], rowCount: data.rowCount ?? 0 };
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      aggregate_query?: string;
      select_query?: string;
    }) => {
      const { data } = await api.post('/api/preset-reports', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'presetReports'] }),
  });
}

export function useUpdateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...rest
    }: {
      id: number;
      name?: string;
      description?: string;
      aggregate_query?: string;
      select_query?: string;
    }) => {
      const { data } = await api.put(`/api/preset-reports/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'presetReports'] }),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/preset-reports/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'presetReports'] }),
  });
}

/* ═══════════════════════════════════════════
 *  10. DOCUMENT RECYCLE BIN
 * ═══════════════════════════════════════════ */

export function useRecycleBin() {
  return useQuery<RecycleBinItem[]>({
    queryKey: ['admin', 'recycleBin'],
    queryFn: async () => {
      const { data } = await api.get('/api/recycle-bin');
      const raw = data.data ?? data.items ?? [];
      return (raw as Record<string, unknown>[]).map((d) => ({
        ...d,
        filename: d.file_name ?? d.filename ?? '',
        file_name: d.file_name ?? d.filename ?? '',
        days_in_bin: d.days_in_bin != null ? Math.round(d.days_in_bin as number) : undefined,
      })) as RecycleBinItem[];
    },
    staleTime: 30_000,
  });
}

/** Compute recycle bin stats client-side (no separate BE stats endpoint) */
export function useRecycleBinStats() {
  const { data: items = [] } = useRecycleBin();
  const stats: RecycleBinStats = {
    total: items.length,
    active: items.filter((i) => i.status === 'active').length,
    restored: items.filter((i) => i.status === 'restored').length,
    expiring_soon: items.filter((i) => (i.days_in_bin ?? 0) > 25).length,
  };
  return { data: stats, isLoading: false };
}

export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/api/recycle-bin/${id}/restore`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'recycleBin'] });
      qc.invalidateQueries({ queryKey: ['admin', 'recycleBinStats'] });
    },
  });
}

export function usePermanentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/recycle-bin/${id}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'recycleBin'] });
      qc.invalidateQueries({ queryKey: ['admin', 'recycleBinStats'] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  11. DA LETTER / DRAFT TEMPLATES
 * ═══════════════════════════════════════════ */

export function useDraftTemplates() {
  return useQuery<DraftTemplate[]>({
    queryKey: ['admin', 'draftTemplates'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/draft-templates');
      return (data.templates ?? data.data ?? []) as DraftTemplate[];
    },
    staleTime: 2 * 60_000,
  });
}

export function useCreateDraftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/admin/draft-templates/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] }),
  });
}

export function useUpdateDraftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      display_name,
      category,
      description,
      document_category_id,
      mark_for_printing_default,
      naming_convention,
    }: {
      id: number;
      display_name?: string;
      category?: string;
      description?: string;
      document_category_id?: number | null;
      mark_for_printing_default?: boolean;
      naming_convention?: string | null;
    }) => {
      const { data } = await api.put(`/api/admin/draft-templates/${id}`, {
        display_name,
        category,
        description,
        document_category_id,
        mark_for_printing_default,
        naming_convention,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] }),
  });
}

export function useDeleteDraftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/draft-templates/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] }),
  });
}

export function useDuplicateDraftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/api/admin/draft-templates/${id}/duplicate`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] }),
  });
}

export function useDraftTemplatePreview(id: number | null) {
  return useQuery<string>({
    queryKey: ['admin', 'draftTemplatePreview', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/draft-templates/${id}/preview`);
      return (data.html ?? data.preview ?? data) as string;
    },
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function useRescanDraftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/api/admin/draft-templates/${id}/rescan`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] }),
  });
}

/** Fetch a single draft template (includes detected_fields + field_mappings) */
export function useDraftTemplate(id: number | null) {
  return useQuery<DraftTemplate>({
    queryKey: ['admin', 'draftTemplate', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/draft-templates/${id}`);
      return (data.template ?? data) as DraftTemplate;
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

/** Save field mappings for a draft template */
export function useSaveDraftFieldMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      mappings,
    }: {
      templateId: number;
      mappings: Record<string, unknown>;
    }) => {
      const { data } = await api.put(`/api/admin/draft-templates/${templateId}/field-mappings`, {
        mappings,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'draftTemplates'] });
      qc.invalidateQueries({ queryKey: ['admin', 'draftTemplate', vars.templateId] });
    },
  });
}

/** Generate a test letter from a draft template */
export function useGenerateDraftTest() {
  return useMutation<
    { blob: Blob; filename: string },
    Error,
    { templateId: number; caseRef: string }
  >({
    mutationFn: async ({ templateId, caseRef }) => {
      const response = await api.post(
        `/api/admin/draft-templates/${templateId}/generate-test`,
        { case_ref: caseRef },
        { responseType: 'blob' },
      );
      const disposition = String(response.headers['content-disposition'] || '');
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `test_${caseRef}.pdf`;
      return { blob: response.data as Blob, filename };
    },
  });
}

/* ═══════════════════════════════════════════
 *  12. DA EMAIL TEMPLATES
 * ═══════════════════════════════════════════ */

export function useEmailTemplates() {
  return useQuery<EmailTemplate[]>({
    queryKey: ['admin', 'emailTemplates'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/da-email-templates');
      const raw = (data.templates ?? data.data ?? []) as Record<string, unknown>[];
      return raw.map((t) => ({
        ...t,
        name: (t.display_name ?? t.name ?? '') as string,
        subject: (t.subject_template ?? t.subject ?? '') as string,
        body: (t.body_html ?? t.body ?? '') as string,
        merge_fields: t.detected_fields as string[] | undefined,
      })) as EmailTemplate[];
    },
    staleTime: 2 * 60_000,
  });
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<EmailTemplate>) => {
      const mapped = {
        display_name: payload.name,
        category: payload.category,
        recipient_type: payload.recipient_type,
        subject_template: payload.subject,
        body_html: payload.body,
      };
      const { data } = await api.post('/api/admin/da-email-templates', mapped);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'emailTemplates'] }),
  });
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: Partial<EmailTemplate> & { id: number }) => {
      const mapped: Record<string, unknown> = {};
      if (rest.name !== undefined) mapped.display_name = rest.name;
      if (rest.category !== undefined) mapped.category = rest.category;
      if (rest.recipient_type !== undefined) mapped.recipient_type = rest.recipient_type;
      if (rest.subject !== undefined) mapped.subject_template = rest.subject;
      if (rest.body !== undefined) mapped.body_html = rest.body;
      const { data } = await api.put(`/api/admin/da-email-templates/${id}`, mapped);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'emailTemplates'] }),
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/da-email-templates/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'emailTemplates'] }),
  });
}

export function useEmailTokens() {
  return useQuery<Record<string, { token: string; description: string }[]>>({
    queryKey: ['admin', 'emailTokens'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/da-email-tokens');
      return data.tokens ?? {};
    },
    staleTime: 10 * 60_000,
  });
}

/* ═══════════════════════════════════════════
 *  13. BLP (Bulk Letter Platform)
 * ═══════════════════════════════════════════ */

export function useBLPTemplates() {
  return useQuery<BLPTemplate[]>({
    queryKey: ['admin', 'blpTemplates'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/blp-templates');
      return (data.templates ?? data.data ?? []) as BLPTemplate[];
    },
    staleTime: 2 * 60_000,
  });
}

/** Fetch a single BLP template with full data (template_data, field_mappings, etc.) */
export function useBLPTemplate(id: number | null) {
  return useQuery<BLPTemplate | null>({
    queryKey: ['admin', 'blpTemplate', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get(`/api/admin/blp-templates/${id}`);
      return (data.template ?? data) as BLPTemplate;
    },
    enabled: !!id,
  });
}

/** Fetch email configurations for a BLP template */
export function useBLPTemplateEmails(templateId: number | null) {
  return useQuery<BLPEmailConfig[]>({
    queryKey: ['admin', 'blpTemplateEmails', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      const { data } = await api.get(`/api/admin/blp-templates/${templateId}/emails`);
      return (data.emails ?? []) as BLPEmailConfig[];
    },
    enabled: !!templateId,
  });
}

/** Save BLP template general settings (name, naming_convention, defendant_address_type) */
export function useUpdateBLPSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      ...settings
    }: {
      templateId: number;
      template_name?: string;
      naming_convention?: string;
      defendant_address_type?: string;
      template_data?: unknown;
    }) => {
      const { data } = await api.put(`/api/admin/blp-templates/${templateId}`, settings);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blpTemplates'] });
      qc.invalidateQueries({ queryKey: ['admin', 'blpTemplate'] });
    },
  });
}

/** Create / update / delete BLP email config */
export function useCreateBLPEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      ...email
    }: { templateId: number } & Partial<BLPEmailConfig>) => {
      const { data } = await api.post(`/api/admin/blp-templates/${templateId}/emails`, email);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplateEmails'] }),
  });
}

export function useUpdateBLPEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      emailId,
      ...email
    }: { templateId: number; emailId: number } & Partial<BLPEmailConfig>) => {
      const { data } = await api.put(
        `/api/admin/blp-templates/${templateId}/emails/${emailId}`,
        email,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplateEmails'] }),
  });
}

export function useDeleteBLPEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, emailId }: { templateId: number; emailId: number }) => {
      const { data } = await api.delete(`/api/admin/blp-templates/${templateId}/emails/${emailId}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplateEmails'] }),
  });
}

export function useCreateBLPTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/admin/blp-templates/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplates'] }),
  });
}

export function useUpdateBLPTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, formData }: { id: number; formData: FormData }) => {
      const { data } = await api.put(`/api/admin/blp-templates/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplates'] }),
  });
}

export function useDeleteBLPTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/blp-templates/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpTemplates'] }),
  });
}

export function useBLPLetterhead() {
  return useQuery<BLPLetterhead | null>({
    queryKey: ['admin', 'blpLetterhead'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/blp-templates/letterhead?format=url');
      /* data could be { letterhead: {...} } or { url, uploaded_at } or { s3_key, url } */
      const lh = data.letterhead ?? data;
      if (!lh || (typeof lh === 'object' && !lh.url && !lh.s3_key)) return null;
      return lh as BLPLetterhead;
    },
    staleTime: 5 * 60_000,
  });
}

export function useUploadBLPLetterhead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/admin/blp-templates/letterhead', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blpLetterhead'] }),
  });
}

export function useBLPFieldDefinitions() {
  return useQuery<
    {
      id?: number;
      name: string;
      key: string;
      field_type?: string;
      description?: string;
      category_name?: string;
      source_table?: string;
    }[]
  >({
    queryKey: ['admin', 'blpFieldDefs'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/blp-templates/field-definitions');
      const raw: unknown[] = data.fields ?? data.data ?? [];
      return raw.map((f: unknown) => {
        const field = f as {
          id?: string | number;
          field_name?: string;
          name?: string;
          field_key?: string;
          key?: string;
          field_type?: string;
          description?: string;
          category_name?: string;
          source_table?: string;
        };
        return {
          id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
          name: field.field_name ?? field.name ?? '',
          key: field.field_key ?? field.key ?? '',
          field_type: field.field_type,
          description: field.description,
          category_name: field.category_name,
          source_table: field.source_table,
        };
      });
    },
    staleTime: 10 * 60_000,
  });
}

export function useBLPFieldMappings(templateId: number | null) {
  return useQuery<Record<string, string>>({
    queryKey: ['admin', 'blpFieldMappings', templateId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/blp-templates/${templateId}/field-mappings`);
      return (data.mappings ?? {}) as Record<string, string>;
    },
    enabled: !!templateId,
    staleTime: 2 * 60_000,
  });
}

export function useSaveBLPFieldMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      mappings,
    }: {
      templateId: number;
      mappings: Record<string, string>;
    }) => {
      const { data } = await api.put(`/api/admin/blp-templates/${templateId}/field-mappings`, {
        mappings,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'blpTemplate', vars.templateId] });
      qc.invalidateQueries({ queryKey: ['admin', 'blpTemplates'] });
    },
  });
}

export function useGenerateBLPTest() {
  return useMutation({
    mutationFn: async ({ templateId, caseRef }: { templateId: number; caseRef: string }) => {
      const res = await api.post(
        `/api/admin/blp-templates/${templateId}/generate-test`,
        { case_ref: caseRef },
        { responseType: 'blob' },
      );
      const filename =
        String(res.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/)?.[1] ||
        `test_letter_${caseRef}.pdf`;
      return { blob: res.data as Blob, filename };
    },
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Connector Templates
 * ═══════════════════════════════════════════ */

export function useConnectorTemplates() {
  return useQuery<ConnectorTemplate[]>({
    queryKey: ['integrations', 'templates'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/integrations/templates');
      return data.data ?? data;
    },
  });
}

export function useConnectorTemplate(id: number | null) {
  return useQuery<ConnectorTemplate>({
    queryKey: ['integrations', 'template', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/integrations/templates/${id}`);
      return data.data ?? data;
    },
    enabled: !!id,
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Connector Instances
 * ═══════════════════════════════════════════ */

export function useConnectorInstances() {
  return useQuery<ConnectorInstance[]>({
    queryKey: ['integrations', 'instances'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/integrations/instances');
      return data.data ?? data;
    },
  });
}

export function useConnectorInstance(id: number | null) {
  return useQuery<ConnectorInstance>({
    queryKey: ['integrations', 'instance', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/integrations/instances/${id}`);
      return data.data ?? data;
    },
    enabled: !!id,
  });
}

export function useCreateConnectorInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      template_id: number;
      name: string;
      config?: Record<string, unknown>;
    }) => {
      const { data } = await api.post('/api/v1/integrations/instances', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'instances'] }),
  });
}

export function useUpdateConnectorInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: number;
      name?: string;
      config?: Record<string, unknown>;
      status?: string;
    }) => {
      const { data } = await api.put(`/api/v1/integrations/instances/${id}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', 'instances'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'instance', vars.id] });
    },
  });
}

export function useDeleteConnectorInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/v1/integrations/instances/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'instances'] }),
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Credentials
 * ═══════════════════════════════════════════ */

export function useSetCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      instanceId,
      fieldName,
      value,
    }: {
      instanceId: number;
      fieldName: string;
      value: string;
    }) => {
      const { data } = await api.put(`/api/v1/integrations/instances/${instanceId}/credentials`, {
        field_name: fieldName,
        value,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', 'instance', vars.instanceId] });
      qc.invalidateQueries({ queryKey: ['integrations', 'instances'] });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ instanceId, fieldName }: { instanceId: number; fieldName: string }) => {
      const { data } = await api.delete(
        `/api/v1/integrations/instances/${instanceId}/credentials/${fieldName}`,
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', 'instance', vars.instanceId] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Test Connection
 * ═══════════════════════════════════════════ */

export function useTestConnection() {
  return useMutation<TestConnectionResult, Error, number>({
    mutationFn: async (instanceId: number) => {
      const { data } = await api.post(`/api/v1/integrations/instances/${instanceId}/test`);
      return data.data ?? data;
    },
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Webhook Endpoints
 * ═══════════════════════════════════════════ */

export function useWebhookEndpoints() {
  return useQuery<WebhookEndpoint[]>({
    queryKey: ['integrations', 'webhookEndpoints'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/integrations/webhooks/endpoints');
      return data.data ?? data;
    },
  });
}

export function useCreateWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      instance_id?: number;
      signature_header?: string;
      event_type_filter?: string[];
      endpoint_mode?: 'form_input' | 'trigger_update';
    }) => {
      const { data } = await api.post('/api/v1/integrations/webhooks/endpoints', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookEndpoints'] }),
  });
}

export function useUpdateWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: number;
      is_active?: boolean;
      event_type_filter?: string[];
      endpoint_mode?: 'form_input' | 'trigger_update';
    }) => {
      const { data } = await api.put(`/api/v1/integrations/webhooks/endpoints/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookEndpoints'] }),
  });
}

export function useDeleteWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/v1/integrations/webhooks/endpoints/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookEndpoints'] }),
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Webhook Subscriptions
 * ═══════════════════════════════════════════ */

export function useWebhookSubscriptions() {
  return useQuery<WebhookSubscription[]>({
    queryKey: ['integrations', 'webhookSubscriptions'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/integrations/webhooks/subscriptions');
      return data.data ?? data;
    },
  });
}

export function useCreateWebhookSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      instance_id?: number;
      name?: string;
      target_url: string;
      event_types: string[];
    }) => {
      const { data } = await api.post('/api/v1/integrations/webhooks/subscriptions', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookSubscriptions'] }),
  });
}

export function useUpdateWebhookSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: number;
      name?: string;
      target_url?: string;
      event_types?: string[];
      is_active?: boolean;
    }) => {
      const { data } = await api.put(`/api/v1/integrations/webhooks/subscriptions/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookSubscriptions'] }),
  });
}

export function useDeleteWebhookSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/v1/integrations/webhooks/subscriptions/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'webhookSubscriptions'] }),
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Webhook Deliveries
 * ═══════════════════════════════════════════ */

export function useWebhookDeliveries(subscriptionId?: number) {
  return useQuery<WebhookDelivery[]>({
    queryKey: ['integrations', 'deliveries', subscriptionId],
    queryFn: async () => {
      const params = subscriptionId ? `?subscription_id=${subscriptionId}` : '';
      const { data } = await api.get(`/api/v1/integrations/webhooks/deliveries${params}`);
      return data.data ?? data;
    },
  });
}

export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deliveryId: number) => {
      const { data } = await api.post(
        `/api/v1/integrations/webhooks/deliveries/${deliveryId}/retry`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'deliveries'] }),
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Webhook Processing / ETL
 * ═══════════════════════════════════════════ */

export function useFieldMappings(endpointId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'fieldMappings', endpointId],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/mappings`,
      );
      return data.data ?? data;
    },
    enabled: !!endpointId,
  });
}

export function useMappingRecords(endpointId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'mappingRecords', endpointId],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/records`,
      );
      return data.data ?? data;
    },
    enabled: !!endpointId,
  });
}

interface BulkRecordPayload {
  record_type: 'contact' | 'case';
  record_label?: string | null;
  sort_order?: number;
  master_field_index?: number | null;
  mappings: Array<{
    source_path: string;
    field_definition_id: number;
    transform?: string | null;
    is_required?: boolean;
    default_value?: string | null;
    sort_order?: number;
  }>;
}

export function useBulkSaveMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      endpointId,
      records,
    }: {
      endpointId: number;
      records: BulkRecordPayload[];
    }) => {
      const { data } = await api.put(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/mappings/bulk`,
        { records },
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', 'fieldMappings', vars.endpointId] });
      qc.invalidateQueries({ queryKey: ['integrations', 'mappingRecords', vars.endpointId] });
    },
  });
}

export function useProcessingConfig(endpointId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'processingConfig', endpointId],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/processing`,
      );
      return data.data ?? data;
    },
    enabled: !!endpointId,
  });
}

export function useUpdateProcessingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      endpointId,
      processing_config,
      is_processing,
    }: {
      endpointId: number;
      processing_config?: Record<string, unknown>;
      is_processing?: boolean;
    }) => {
      const { data } = await api.put(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/processing`,
        { processing_config, is_processing },
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', 'processingConfig', vars.endpointId] });
      qc.invalidateQueries({ queryKey: ['integrations', 'webhookEndpoints'] });
    },
  });
}

export function useRecaptureSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (endpointId: number) => {
      const { data } = await api.post(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/recapture-sample`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'webhookEndpoints'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'processingConfig'] });
    },
  });
}

export function useProcessingLogs(endpointId: number | null, page = 0, limit = 25) {
  return useQuery<{ rows: unknown[]; total: number; limit: number; offset: number }>({
    queryKey: ['integrations', 'processingLogs', endpointId, page, limit],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/endpoints/${endpointId}/processing-logs`,
        {
          params: { limit, offset: page * limit },
        },
      );
      return data as { rows: unknown[]; total: number; limit: number; offset: number };
    },
    enabled: !!endpointId,
  });
}

export function useLookupCaseTypes() {
  return useQuery({
    queryKey: ['integrations', 'lookupCaseTypes'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/integrations/webhooks/lookup/case-types');
      return data.data ?? data;
    },
  });
}

export function useLookupStages(caseTypeId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'lookupStages', caseTypeId],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/lookup/case-types/${caseTypeId}/stages`,
      );
      return data.data ?? data;
    },
    enabled: !!caseTypeId,
  });
}

export function useLookupFieldDefinitions(caseTypeId?: number) {
  return useQuery({
    queryKey: ['integrations', 'lookupFieldDefinitions', caseTypeId],
    queryFn: async () => {
      const params = caseTypeId ? `?case_type_id=${caseTypeId}` : '';
      const { data } = await api.get(
        `/api/v1/integrations/webhooks/lookup/field-definitions${params}`,
      );
      return data.data ?? data;
    },
  });
}

/* ═══════════════════════════════════════════
 *  INTEGRATIONS — Audit Logs
 * ═══════════════════════════════════════════ */

export function useIntegrationAuditLogs(filters?: {
  entity_type?: string;
  entity_id?: number;
  limit?: number;
}) {
  return useQuery<IntegrationAuditEntry[]>({
    queryKey: ['integrations', 'auditLogs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.entity_type) params.set('entity_type', filters.entity_type);
      if (filters?.entity_id) params.set('entity_id', String(filters.entity_id));
      if (filters?.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const { data } = await api.get(`/api/v1/integrations/audit-logs${qs ? `?${qs}` : ''}`);
      return data.data ?? data;
    },
  });
}

/**
 * Fetch the OAuth2 authorization URL for an OAuth2 connector instance.
 * Returns { authorize_url, redirect_uri } — caller opens authorize_url in a new window.
 */
export function useInitiateOAuth() {
  return useMutation({
    mutationFn: async (instanceId: number) => {
      const { data } = await api.get<{ authorize_url: string; redirect_uri: string }>(
        `/api/v1/integrations/instances/${instanceId}/oauth/authorize`,
      );
      return data;
    },
  });
}

export function useConnectorInstanceLogs(instanceId: number | null, enabled = false) {
  return useQuery<IntegrationAuditEntry[]>({
    queryKey: ['integrations', 'instanceLogs', instanceId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/integrations/instances/${instanceId}/logs?limit=100`);
      return data;
    },
    enabled: enabled && instanceId !== null,
    staleTime: 0,
  });
}

export function useBLPBulkCaseCount(caseType: string | null, stageId: number | null) {
  return useQuery<{ count: number }>({
    queryKey: ['admin', 'blpBulkCaseCount', caseType, stageId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (caseType) params.append('caseType', caseType);
      if (stageId) params.append('stageId', String(stageId));
      const { data } = await api.get(`/api/admin/blp-templates/bulk/cases?${params.toString()}`);
      return { count: data.count ?? data.total ?? 0 };
    },
    enabled: !!caseType && !!stageId,
    staleTime: 30_000,
  });
}

export function useBLPBulkGenerate() {
  return useMutation({
    mutationFn: async (payload: {
      templateId: number;
      caseType: string;
      stageId: number;
      addToCaseFiles?: boolean;
      changeStage?: boolean;
      targetStageId?: number;
      sendEmails?: boolean;
      setLBADate?: boolean;
      lbaDate?: string;
    }) => {
      const { data } = await api.post('/api/admin/blp-templates/bulk/generate', payload);
      return data;
    },
  });
}

export function useBLPBulkStatus(jobId: string | null) {
  return useQuery<BulkJob>({
    queryKey: ['admin', 'blpBulkStatus', jobId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/blp-templates/bulk/status/${jobId}`);
      return data as BulkJob;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const d = query.state.data as BulkJob | undefined;
      if (d && (d.status === 'complete' || d.status === 'failed' || d.status === 'cancelled'))
        return false;
      return 3000; // poll every 3s while active
    },
  });
}

export function useCancelBLPBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await api.post(`/api/admin/blp-templates/bulk/cancel/${jobId}`);
      return data;
    },
    onSuccess: (_d, jobId) => qc.invalidateQueries({ queryKey: ['admin', 'blpBulkStatus', jobId] }),
  });
}

export function useBLPJobHistory(limit = 50, offset = 0) {
  return useQuery<BulkJobHistoryResponse>({
    queryKey: ['admin', 'blpJobHistory', limit, offset],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/blp-templates/bulk/history?limit=${limit}&offset=${offset}`,
      );
      return data as BulkJobHistoryResponse;
    },
    staleTime: 30_000,
  });
}

/* ═══════════════════════════════════════════
 *  DATA QUALITY
 * ═══════════════════════════════════════════ */

export function useDQIssueSummary() {
  return useQuery<import('../types/admin').DataQualityIssueSummary>({
    queryKey: ['admin', 'dq', 'summary'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/issues/summary');
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useDQIssues(
  status: string = 'open',
  matchType?: string,
  limit = 50,
  offset = 0,
  sort = 'confidence',
) {
  return useQuery<{ issues: import('../types/admin').DataQualityIssue[]; total: number }>({
    queryKey: ['admin', 'dq', 'issues', status, matchType, limit, offset, sort],
    queryFn: async () => {
      const params = new URLSearchParams({
        status,
        limit: String(limit),
        offset: String(offset),
        sort,
      });
      if (matchType) params.set('matchType', matchType);
      const { data } = await api.get(`/api/admin/data-quality/issues?${params}`);
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useRunDuplicateScan() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/admin/data-quality/scan');
      return data.data; // { status: 'running', startedBy, startedAt }
    },
    onSuccess: () => {
      // Don't invalidate yet — scan is running in background
    },
  });
}

export function useScanStatus(enabled: boolean) {
  return useQuery<{
    status: string;
    startedBy?: string;
    startedAt?: string;
    result?: import('../types/admin').DuplicateScanResult;
    error?: string;
  }>({
    queryKey: ['admin', 'dq', 'scanStatus'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/scan/status');
      return data.data;
    },
    enabled,
    refetchInterval: enabled ? 2000 : false,
    staleTime: 0,
  });
}

export function useDismissIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) => {
      const { data } = await api.put(`/api/admin/data-quality/issues/${id}/dismiss`, { note });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

export function useReopenIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.put(`/api/admin/data-quality/issues/${id}/reopen`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

/* ═══════════════════════════════════════════
 *  CLIENT PORTAL ADMIN
 * ═══════════════════════════════════════════ */

export function usePortalDashboardStats() {
  return useQuery<PortalDashboardStats>({
    queryKey: ['admin', 'portal', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/api/portal-admin/stats');
      return data;
    },
  });
}

export function usePortalUsers(params?: { search?: string; status?: string; page?: number }) {
  return useQuery<{ users: PortalUser[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'portal', 'users', params],
    queryFn: async () => {
      const { data } = await api.get('/api/portal-admin/users', { params });
      return data;
    },
  });
}

export function useDeactivatePortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.post(`/api/portal-admin/users/${userId}/deactivate`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal'] });
    },
  });
}

export function useReactivatePortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.post(`/api/portal-admin/users/${userId}/reactivate`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal'] });
    },
  });
}

export function useResetPortalUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.post(`/api/portal-admin/users/${userId}/reset-password`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal'] });
    },
  });
}

export function usePortalUserSessions(userId: number | null) {
  return useQuery<{ sessions: PortalUserSession[] }>({
    queryKey: ['admin', 'portal', 'sessions', userId],
    queryFn: async () => {
      const { data } = await api.get(`/api/portal-admin/users/${userId}/sessions`);
      return data;
    },
    enabled: !!userId,
  });
}

export function useForceLogoutPortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.post(`/api/portal-admin/users/${userId}/force-logout`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal'] });
    },
  });
}

export function useDeletePortalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.delete(`/api/portal-admin/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal'] });
    },
  });
}

export function usePortalEmailTemplates() {
  return useQuery<PortalEmailTemplate[]>({
    queryKey: ['admin', 'portal', 'email-templates'],
    queryFn: async () => {
      const { data } = await api.get('/api/portal-admin/email-templates');
      return data.templates;
    },
  });
}

export function useUpdatePortalEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: number;
      name: string;
      subject: string;
      html_body: string;
      text_body?: string;
    }) => {
      const { id, ...body } = payload;
      const { data } = await api.put(`/api/portal-admin/email-templates/${id}`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal', 'email-templates'] });
    },
  });
}

export function usePreviewPortalEmailTemplate() {
  return useMutation<{ subject: string; html: string }, Error, number>({
    mutationFn: async (templateId: number) => {
      const { data } = await api.get(`/api/portal-admin/email-templates/${templateId}/preview`);
      return data;
    },
  });
}

export function usePortalBranding() {
  return useQuery<PortalBranding>({
    queryKey: ['admin', 'portal', 'branding'],
    queryFn: async () => {
      const { data } = await api.get('/api/portal-admin/branding');
      return data;
    },
  });
}

export function useUpdatePortalBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<PortalBranding>) => {
      const { data } = await api.put('/api/portal-admin/branding', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'portal', 'branding'] });
    },
  });
}

export function usePortalAuditLog(params?: {
  user_id?: number;
  action?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
}) {
  return useQuery<{
    logs: PortalAuditEntry[];
    total: number;
    page: number;
    limit: number;
    action_types: string[];
  }>({
    queryKey: ['admin', 'portal', 'audit-log', params],
    queryFn: async () => {
      const { data } = await api.get('/api/portal-admin/audit-log', { params });
      return data;
    },
  });
}

export function useMergeContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      masterContactId: string;
      mergedContactId: string;
      issueId?: number;
    }) => {
      const { data } = await api.post('/api/admin/data-quality/merge', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

export function useMergeHistory(limit = 50, offset = 0) {
  return useQuery<{ merges: import('../types/admin').ContactMergeLog[]; total: number }>({
    queryKey: ['admin', 'dq', 'mergeHistory', limit, offset],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/data-quality/merge-history?limit=${limit}&offset=${offset}`,
      );
      return data.data;
    },
    staleTime: 60_000,
  });
}

export function useMissingDataStats() {
  return useQuery<import('../types/admin').MissingDataStats>({
    queryKey: ['admin', 'dq', 'missingData'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/missing-data');
      return data.data;
    },
    staleTime: 60_000,
  });
}

export function useMissingDataContacts(field: string, limit = 50, offset = 0, enabled = true) {
  return useQuery<{ contacts: import('../types/admin').MissingDataContact[]; total: number }>({
    queryKey: ['admin', 'dq', 'missingData', field, limit, offset],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/data-quality/missing-data/${field}?limit=${limit}&offset=${offset}`,
      );
      return data.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

// ── Cluster hooks ──────────────────────────────────────────────

export function useClusterSummary() {
  return useQuery<import('../types/admin').ClusterSummary>({
    queryKey: ['admin', 'dq', 'clusterSummary'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/clusters/summary');
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useClusters(status: string = 'open', limit = 20, offset = 0, sort = 'size') {
  return useQuery<{ clusters: import('../types/admin').DuplicateCluster[]; total: number }>({
    queryKey: ['admin', 'dq', 'clusters', status, limit, offset, sort],
    queryFn: async () => {
      const params = new URLSearchParams({
        status,
        limit: String(limit),
        offset: String(offset),
        sort,
      });
      const { data } = await api.get(`/api/admin/data-quality/clusters?${params}`);
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useClusterDetails(clusterId: number | null) {
  return useQuery<import('../types/admin').ClusterDetail>({
    queryKey: ['admin', 'dq', 'cluster', clusterId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/data-quality/clusters/${clusterId}`);
      return data.data;
    },
    enabled: clusterId !== null,
    staleTime: 30_000,
  });
}

export function useDismissCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) => {
      const { data } = await api.put(`/api/admin/data-quality/clusters/${id}/dismiss`, { note });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

export function useMergeCluster() {
  const qc = useQueryClient();
  return useMutation<
    import('../types/admin').ClusterMergeResult,
    Error,
    { clusterId: number; masterContactId: string; contactIds?: string[] }
  >({
    mutationFn: async ({ clusterId, masterContactId, contactIds }) => {
      const { data } = await api.post(`/api/admin/data-quality/clusters/${clusterId}/merge`, {
        masterContactId,
        contactIds,
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

// ── Case duplicate hooks ───────────────────────────────────────

export function useRunCaseDuplicateScan() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/admin/data-quality/case-scan');
      return data.data;
    },
  });
}

export function useCaseScanStatus(enabled: boolean) {
  return useQuery<{
    status: string;
    startedBy?: string;
    startedAt?: string;
    result?: import('../types/admin').CaseDuplicateScanResult;
    error?: string;
  }>({
    queryKey: ['admin', 'dq', 'caseScanStatus'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/case-scan/status');
      return data.data;
    },
    enabled,
    refetchInterval: enabled ? 2000 : false,
    staleTime: 0,
  });
}

export function useCaseDuplicateGroupSummary() {
  return useQuery<import('../types/admin').CaseDuplicateGroupSummary>({
    queryKey: ['admin', 'dq', 'caseDupeSummary'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/data-quality/case-duplicates/summary');
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useCaseDuplicateGroups(status = 'open', limit = 20, offset = 0, sort = 'count') {
  return useQuery<{ groups: import('../types/admin').CaseDuplicateGroup[]; total: number }>({
    queryKey: ['admin', 'dq', 'caseDupeGroups', status, limit, offset, sort],
    queryFn: async () => {
      const params = new URLSearchParams({
        status,
        limit: String(limit),
        offset: String(offset),
        sort,
      });
      const { data } = await api.get(`/api/admin/data-quality/case-duplicates?${params}`);
      return data.data;
    },
    staleTime: 30_000,
  });
}

export function useCaseDuplicateGroupDetails(groupId: number | null) {
  return useQuery<import('../types/admin').CaseDuplicateGroupDetail>({
    queryKey: ['admin', 'dq', 'caseDupeGroup', groupId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/data-quality/case-duplicates/${groupId}`);
      return data.data;
    },
    enabled: groupId !== null,
    staleTime: 30_000,
  });
}

export function useDismissCaseDuplicateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) => {
      const { data } = await api.put(`/api/admin/data-quality/case-duplicates/${id}/dismiss`, {
        note,
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'dq'] });
    },
  });
}

/* ═══════════════════════════════════════════════════════════
 *  FIRM BRANDING
 * ═══════════════════════════════════════════════════════════ */

/** Fetch all firm branding keys. Available to any authenticated user. */
export function useFirmBranding() {
  return useQuery<FirmBrandingMap>({
    queryKey: ['admin', 'firmBranding'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/firm-branding');
      return (data.data ?? {}) as FirmBrandingMap;
    },
    staleTime: 5 * 60_000,
  });
}

/** Bulk-upsert firm branding values. Superadmin only. */
export function useUpdateFirmBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: FirmBrandingUpdate[]) => {
      const { data } = await api.put('/api/admin/firm-branding', { updates });
      return (data.data ?? {}) as FirmBrandingMap;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'firmBranding'] });
    },
  });
}

/** GET /logo/:slot — returns signed URL for header or email logo, or null if none set. */
export function useFirmLogo(slot: 'header' | 'email') {
  return useQuery<FirmLogo | null>({
    queryKey: ['admin', 'firmLogo', slot],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/firm-branding/logo/${slot}`);
      return data.exists ? (data as FirmLogo) : null;
    },
    staleTime: 60_000,
  });
}

/** POST /logo/:slot — upload an image file as the header or email logo. */
export function useUploadFirmLogo(slot: 'header' | 'email') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post(`/api/admin/firm-branding/logo/${slot}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'firmLogo', slot] });
      qc.invalidateQueries({ queryKey: ['admin', 'firmBranding'] });
    },
  });
}

/** DELETE /logo/:slot — remove the header or email logo. */
export function useDeleteFirmLogo(slot: 'header' | 'email') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/api/admin/firm-branding/logo/${slot}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'firmLogo', slot] });
      qc.invalidateQueries({ queryKey: ['admin', 'firmBranding'] });
    },
  });
}

/** GET /letterhead — returns signed URL for the letterhead PDF, or null if none uploaded. */
export function useFirmLetterhead() {
  return useQuery<FirmLetterhead | null>({
    queryKey: ['admin', 'firmLetterhead'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/firm-branding/letterhead?format=url');
      return data.exists ? (data as FirmLetterhead) : null;
    },
    staleTime: 5 * 60_000,
  });
}

/** POST /letterhead — upload a replacement letterhead PDF. */
export function useUploadFirmLetterhead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/api/admin/firm-branding/letterhead', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'firmLetterhead'] }),
  });
}

/** DELETE /letterhead — remove the letterhead PDF. */
export function useDeleteFirmLetterhead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete('/api/admin/firm-branding/letterhead');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'firmLetterhead'] }),
  });
}

// ══════════════════════════════════════════════════════════════════════════════
// Document Ledger — Phase 2
// ══════════════════════════════════════════════════════════════════════════════

// ── Document events timeline ──────────────────────────────────────────────────

export function useDocumentEvents(documentId: string | number | null) {
  return useQuery<import('../types/admin').DocumentEvent[]>({
    queryKey: ['admin', 'ledger', 'events', documentId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/documents/${documentId}/events`);
      return data.events;
    },
    enabled: !!documentId,
    staleTime: 10_000,
  });
}

// ── Content hash deduplication ────────────────────────────────────────────────

export function useCheckDuplicate(contentHash: string | null, enabled = true) {
  return useQuery<import('../types/admin').DuplicateCheckResult>({
    queryKey: ['admin', 'ledger', 'duplicate', contentHash],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/documents/check-duplicate?contentHash=${contentHash}`,
      );
      return { isDuplicate: data.isDuplicate, duplicate: data.duplicate };
    },
    enabled: enabled && !!contentHash,
    staleTime: 30_000,
  });
}

// ── Document direction ────────────────────────────────────────────────────────

export function useUpdateDirection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, direction }: { documentId: string; direction: string }) => {
      const { data } = await api.patch(`/api/admin/documents/${documentId}/direction`, {
        direction,
      });
      return data;
    },
    onSuccess: (_data, { documentId }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'ledger', 'events', documentId] });
      qc.invalidateQueries({ queryKey: ['admin', 'ledger'] });
    },
  });
}

// ── Required document rules ───────────────────────────────────────────────────

export function useRequiredDocumentRules() {
  return useQuery<import('../types/admin').RequiredDocumentRule[]>({
    queryKey: ['admin', 'requiredDocumentRules'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/required-document-rules');
      return data.rules;
    },
    staleTime: 60_000,
  });
}

export function useDocumentGaps(params?: { caseTypeId?: number; categoryId?: number }) {
  const search = new URLSearchParams();
  if (params?.caseTypeId) search.set('caseTypeId', String(params.caseTypeId));
  if (params?.categoryId) search.set('categoryId', String(params.categoryId));
  const qs = search.toString() ? `?${search.toString()}` : '';
  return useQuery<{ gaps: import('../types/admin').DocumentGap[]; totalGaps: number }>({
    queryKey: ['admin', 'documentGaps', params],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/required-document-rules/gaps${qs}`);
      return { gaps: data.gaps, totalGaps: data.totalGaps };
    },
    staleTime: 30_000,
  });
}

export function useCreateRequiredDocumentRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      case_type_id?: number | null;
      stage_id?: number | null;
      category_id: number;
      description?: string;
    }) => {
      const { data } = await api.post('/api/admin/required-document-rules', body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'requiredDocumentRules'] }),
  });
}

export function useUpdateRequiredDocumentRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number; [k: string]: unknown }) => {
      const { data } = await api.put(`/api/admin/required-document-rules/${id}`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'requiredDocumentRules'] }),
  });
}

export function useDeleteRequiredDocumentRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/required-document-rules/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'requiredDocumentRules'] }),
  });
}

// ── Filename pattern rules ────────────────────────────────────────────────────

export function useFilenamePatternRules() {
  return useQuery<import('../types/admin').FilenamePatternRule[]>({
    queryKey: ['admin', 'filenamePatternRules'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/filename-pattern-rules');
      return data.rules;
    },
    staleTime: 60_000,
  });
}

export function useMatchFilenamePatterns() {
  return useMutation<import('../types/admin').FilenameMatchResult[], unknown, string[]>({
    mutationFn: async (filenames: string[]) => {
      const { data } = await api.post('/api/admin/filename-pattern-rules/match', { filenames });
      return data.matches;
    },
  });
}

export function useCreateFilenamePatternRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      pattern: string;
      is_regex?: boolean;
      category_id: number;
      description?: string;
      priority?: number;
      is_active?: boolean;
    }) => {
      const { data } = await api.post('/api/admin/filename-pattern-rules', body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'filenamePatternRules'] }),
  });
}

export function useUpdateFilenamePatternRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number; [k: string]: unknown }) => {
      const { data } = await api.put(`/api/admin/filename-pattern-rules/${id}`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'filenamePatternRules'] }),
  });
}

export function useDeleteFilenamePatternRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/api/admin/filename-pattern-rules/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'filenamePatternRules'] }),
  });
}

/* ═══════════════════════════════════════════
 *  STAGE GUARDS
 * ═══════════════════════════════════════════ */

export function useAllStageGuards() {
  return useQuery<StageGuard[]>({
    queryKey: ['admin', 'stageGuards'],
    queryFn: async () => {
      const { data } = await api.get('/api/stage-guards');
      return (data.guards ?? []) as StageGuard[];
    },
    staleTime: 60_000,
  });
}

export function useStageGuardsByStage(stageId: number | null) {
  return useQuery<StageGuard[]>({
    queryKey: ['admin', 'stageGuards', 'stage', stageId],
    queryFn: async () => {
      const { data } = await api.get(`/api/stage-guards/stage/${stageId}`);
      return (data.guards ?? []) as StageGuard[];
    },
    enabled: !!stageId,
    staleTime: 30_000,
  });
}

export function useCreateStageGuard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<StageGuard>) => {
      const { data } = await api.post('/api/stage-guards', payload);
      return data.guard as StageGuard;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stageGuards'] });
      if (variables.stage_id) {
        qc.invalidateQueries({ queryKey: ['admin', 'stageGuards', 'stage', variables.stage_id] });
      }
    },
  });
}

export function useUpdateStageGuard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ guard_id, ...rest }: Partial<StageGuard> & { guard_id: number }) => {
      const { data } = await api.put(`/api/stage-guards/${guard_id}`, rest);
      return data.guard as StageGuard;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stageGuards'] });
      if (variables.stage_id) {
        qc.invalidateQueries({ queryKey: ['admin', 'stageGuards', 'stage', variables.stage_id] });
      }
    },
  });
}

export function useDeleteStageGuard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (guard_id: number) => {
      await api.delete(`/api/stage-guards/${guard_id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'stageGuards'] }),
  });
}
